import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { addDoc, collection, doc, getDocs } from "firebase/firestore";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db } from "../Config/firebase";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import RelatorioAdesividadePrint from "./RelatorioAdesividadePrint";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const estadoInicial = {
  codigo: "",
  cliente: "",
  clienteId: "",
  obra: "",
  obraId: "",
  material: "",
  materialId: "",
  codigoMaterial: "",
  fornecedor: "",
  localColeta: "",
  dataEnsaio: new Date().toISOString().slice(0, 10),
};

function calcularProximoCodigo(lista) {
  const numeros = lista
    .map((item) => Number(String(item.codigo || "").match(/\d+/)?.[0]))
    .filter(Number.isFinite);
  return String((numeros.length ? Math.max(...numeros) : 0) + 1).padStart(3, "0");
}

function formatarDataBR(data) {
  if (!data) return "-";
  const [ano, mes, dia] = String(data).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : String(data);
}

export default function CadastroAdesividade() {
  const history = useHistory();
  const [ensaios, setEnsaios] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [materiais, setMateriais] = useState([]);
  const [obras, setObras] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [form, setForm] = useState(estadoInicial);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [ensaioPrint, setEnsaioPrint] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const [erroCarregamento, setErroCarregamento] = useState("");

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    try {
      setErroCarregamento("");
      const [ensaiosSnap, materiaisSnap, obrasSnap] = await Promise.all([
        getDocs(consultaTenantAtual(collection(db, "tblAdesividadeLiganteBetuminoso"))),
        getDocs(consultaTenantAtual(collection(db, "tblListaMateriaisCadastrados"))),
        getDocs(consultaTenantAtual(collection(db, "obras"))),
      ]);
      setEnsaios(
        ensaiosSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => {
            const dataA = a.criadoEm?.toMillis?.() || a.criadoEm?.seconds || 0;
            const dataB = b.criadoEm?.toMillis?.() || b.criadoEm?.seconds || 0;
            return dataB - dataA;
          })
      );
      setMateriais(
        materiaisSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => Number(b.codigo || 0) - Number(a.codigo || 0))
      );
      setObras(obrasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (falha) {
      console.error("Erro ao carregar cadastro de adesividade:", falha);
      setErroCarregamento("Não foi possível carregar os dados de adesividade. Verifique as permissões e tente novamente.");
    } finally {
      setCarregandoLista(false);
    }
  }

  function abrirNovo() {
    setEditando(null);
    setForm({
      ...estadoInicial,
      dataEnsaio: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  }

  function abrirEdicao(item) {
    setEditando(item);
    setForm({ ...estadoInicial, ...item });
    setOpen(true);
  }

  function selecionarMaterial(material) {
    const obra = obras.find((item) => item.id === material?.ObraId);
    setForm((anterior) => ({
      ...anterior,
      cliente: material?.Cliente || "",
      clienteId: material?.ClienteId || obra?.clienteId || "",
      obra: obra?.nome || "",
      obraId: material?.ObraId || "",
      material: material?.Descricao || "",
      materialId: material?.id || "",
      codigoMaterial: material?.codigo || "",
      fornecedor: material?.Fornecedor || "",
      localColeta: material?.LocalColeta || "",
    }));
  }

  async function salvar() {
    if (!form.materialId) {
      alert("Selecione o material.");
      return;
    }

    setSalvando(true);
    const codigo = editando?.id
      ? form.codigo
      : form.codigo || (await gerarSequencial({
          chave: "adesividade",
          formato: { padding: 3 },
          valorMinimo: obterMaiorSequencial(ensaios),
        })).codigo;
    const dados = { ...form, codigo, atualizadoEm: new Date() };
    let id = editando?.id;

    if (id) {
      await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblAdesividadeLiganteBetuminoso", id),
        original: editando,
        atual: { ...editando, ...dados },
        metadados: { atualizadoEm: dados.atualizadoEm },
      });
    } else {
      const ref = await addDoc(collection(db, "tblAdesividadeLiganteBetuminoso"), aplicarTenantAtual({
        ...dados,
        ligantes: [
          {
            id: `ligante-${Date.now()}`,
            nome: "",
            resultado: "",
            amostras: [{}, {}],
          },
        ],
        criadoEm: new Date(),
      }));
      id = ref.id;
    }

    setSalvando(false);
    setOpen(false);
    await carregarDados();
    history.push(`/app/adesividade-ligante-betuminoso/${id}`, { modoEdicao: true });
  }

  function abrirRelatorio(item, imprimir = false) {
    setAutoPrint(imprimir);
    setEnsaioPrint(item);
  }

  const lista = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return ensaios;
    return ensaios.filter((item) =>
      [item.codigo, item.cliente, item.obra, item.material, item.fornecedor]
        .some((valor) => contemTextoPesquisa(String(valor || "").toLowerCase(), termo))
    );
  }, [ensaios, filtro]);

  if (ensaioPrint) {
    return (
      <RelatorioAdesividadePrint
        ensaio={ensaioPrint}
        autoPrint={autoPrint}
        onVoltar={() => {
          setEnsaioPrint(null);
          setAutoPrint(false);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {erroCarregamento && (
          <Alert severity="error" sx={{ mb: 2 }}>{erroCarregamento}</Alert>
        )}
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Adesividade ao Ligante Betuminoso</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Cadastro dos ensaios de agregado graúdo</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirNovo}>Novo Ensaio</Button>
          <Button variant="outlined" onClick={() => history.push("/app/relatorios/adesividade-ligante-betuminoso")}>
            Relatório Adesividade
          </Button>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(event) => setFiltro(event.target.value)}
            sx={{ flex: 1 }}
          />
        </Stack>
        </Paper>

        <Paper sx={{ overflow: "auto", border: "1px solid #e2e8f0", boxShadow: "none" }}>
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 850 }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Material</TableCell>
                <TableCell>Data</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => history.push(`/app/adesividade-ligante-betuminoso/${item.id}`)}
                >
                  <TableCell>{item.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="lista-dado-principal">{item.cliente || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obra || "-"}</div>
                  </TableCell>
                  <TableCell className="lista-dado-destaque">{item.material || "-"}</TableCell>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(item.dataEnsaio)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar relatório">
                      <IconButton size="small" onClick={(event) => {
                        event.stopPropagation();
                        abrirRelatorio(item, false);
                      }}>
                        <VisibilityIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar cadastro">
                      <IconButton size="small" onClick={(event) => {
                        event.stopPropagation();
                        abrirEdicao(item);
                      }}>
                        <EditIcon color="info" />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro colecao="tblAdesividadeLiganteBetuminoso" documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              ))}
              {carregandoLista && <TableRow><TableCell colSpan={5}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!carregandoLista && lista.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center">Nenhum ensaio cadastrado.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editando ? "Editar cadastro" : "Novo ensaio de adesividade"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Código" value={form.codigo} onChange={(event) => setForm({ ...form, codigo: event.target.value })} />
            <Autocomplete
              options={materiais}
              value={materiais.find((item) => item.id === form.materialId) || null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.codigo || ""} - ${option.Cliente || ""} - ${option.Descricao || ""}`}
              onChange={(event, value) => selecionarMaterial(value)}
              renderInput={(params) => <TextField {...params} label="Material" />}
            />
            <TextField label="Cliente" value={form.cliente} InputProps={{ readOnly: true }} />
            <TextField label="Obra" value={form.obra} InputProps={{ readOnly: true }} />
            <TextField label="Fornecedor / Procedência" value={form.fornecedor} InputProps={{ readOnly: true }} />
            <TextField label="Local da coleta" value={form.localColeta} onChange={(event) => setForm({ ...form, localColeta: event.target.value })} />
            <TextField type="date" label="Data do ensaio" value={form.dataEnsaio} onChange={(event) => setForm({ ...form, dataEnsaio: event.target.value })} InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar e continuar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
